/**
 * Formatting helpers for the edit-timesheet2 page.
 *
 * Templates render values, they never calculate them (see CLAUDE.md), so every
 * duration, clock time, percentage and pluralised count on the page comes from a
 * named function here.
 *
 * Conventions:
 *	- Durations are minutes, displayed as "Xh Ym" / "Ym" (matching
 *	  `helpers/timeHelpers.formatMinutesForDisplay`, which the old page uses).
 *	- A "day minute" is minutes from midnight on the timesheet's reporting date.
 *	  An entry that runs past midnight therefore has a day minute above 1440,
 *	  which keeps ordering, overlap detection and split maths correct.
 */


const AM_PM_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*(AM|PM)?$/i;

const MINUTES_PER_DAY = 24 * 60;




/**
 * "1h 30m" / "45m" / "2h" / "0m", and the seconds-to-minutes rounding, live in the shared
 * `helpers/duration-formatting.js` (the Approvals workspace uses them too). They are
 * re-exported here under their existing names so this page's callers don't change.
 *
 * Seconds are the unit of truth for every duration on this page: a total is
 * accumulated in seconds and rounded exactly once, at the point it is
 * displayed. Rounding each captured block to a minute and then adding the
 * minutes up loses up to 30 seconds per block, which on a fragmented day put
 * the page several minutes below the API's own figure for the same bucket.
 *
 * `roundSecondsToMinutes` matches the API's group-level rounding (`Math.Round(TotalSeconds / 60)`
 * in `LoadTimesheetExtService.LoadCalculatedTimesheetBucketAllocationGroups`),
 * so a total computed here agrees with `TimesheetBucketAllocationGroup.TotalMinutes`.
 * Note the API rounds a single entry differently again — `TimesheetBucket.Ext.DurationMinutes`
 * uses a configurable "seconds threshold" (20s, not 30s) — which is why a sum is
 * never built out of per-entry minutes from either side.
 */
import { formatMinutes, roundSecondsToMinutes } from "/helpers/duration-formatting.js";

export { formatMinutes, roundSecondsToMinutes };




/**
 * Format a number of seconds as a duration, rounded to the nearest minute.
 * Used for entry totals, where the API supplies seconds.
 */
export function formatSecondsAsMinutes(seconds)
{
	return formatMinutes(roundSecondsToMinutes(seconds));
}




/**
 * Format a block's seconds as a duration, reading "<1m" for anything under a
 * minute instead of "0m" (or a rounded-up "1m"). Sub-minute blocks are a
 * capture/generation quirk that the user needs to see and allocate, not time
 * that should be rounded away. Used for entry rows and the unallocated counts.
 */
export function formatSecondsAsEntryDuration(seconds)
{
	if ((seconds || 0) < 60) return "<1m";

	return formatSecondsAsMinutes(seconds);
}




/**
 * Format a number of seconds at second precision — "26m 50s" / "26m" / "40s".
 * Used for the captured-activity rows inside an expanded entry.
 */
export function formatSecondsPrecise(seconds)
{
	const total = Math.max(0, Math.round(seconds || 0));
	const mins = Math.floor(total / 60);
	const secs = total % 60;

	if (!mins) return secs + "s";
	if (!secs) return mins + "m";
	return mins + "m " + secs + "s";
}




/**
 * Format a day minute as a 12-hour clock time — "9:38 AM".
 * Day minutes past midnight wrap, so 1470 reads as "12:30 AM".
 */
export function formatTimeOfDay(dayMinutes)
{
	const minuteOfDay = ((Math.round(dayMinutes || 0) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

	let hours = Math.floor(minuteOfDay / 60);
	const mins = minuteOfDay % 60;
	const period = hours >= 12 ? "PM" : "AM";

	hours = hours % 12;
	if (hours === 0) hours = 12;

	return hours + ":" + String(mins).padStart(2, "0") + " " + period;
}




/**
 * Format a day minute with seconds — "9:38:24 AM".
 * Used for the chronological captured-activity rows.
 */
export function formatTimeOfDayWithSeconds(daySeconds)
{
	const total = Math.max(0, Math.round(daySeconds || 0));
	const seconds = total % 60;

	return formatTimeOfDay(Math.floor(total / 60)).replace(" ", ":" + String(seconds).padStart(2, "0") + " ");
}




/**
 * Format a day minute as a 24-hour clock value — "09:38".
 * This is the shape the Timesheets API takes for local times (split points,
 * manual-entry start times), matching the `<input type="time">` value the old
 * page sends.
 */
export function formatClock24(dayMinutes)
{
	const minuteOfDay = ((Math.round(dayMinutes || 0) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

	return String(Math.floor(minuteOfDay / 60)).padStart(2, "0") + ":" + String(minuteOfDay % 60).padStart(2, "0");
}




/**
 * Format a start/end pair as "9:38 AM – 10:23 AM".
 */
export function formatTimeRange(startDayMinutes, endDayMinutes)
{
	return formatTimeOfDay(startDayMinutes) + " – " + formatTimeOfDay(endDayMinutes);
}




/**
 * Parse a local date/time from the API into a `Date`.
 *
 * The Timesheets API returns several shapes for the same idea
 * ("2025-10-09 09:38", "2025-10-09 09:38:02.28234 AM"), and `new Date(...)` only
 * handles some of them consistently across browsers, so the common shapes are
 * matched explicitly and `Date` is the last resort.
 *
 * @returns {Date|null} `null` when the value is missing or unparseable.
 */
export function parseLocalDateTime(value)
{
	if (!value) return null;
	if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

	const match = String(value).trim().match(AM_PM_PATTERN);

	if (match)
	{
		let hours = parseInt(match[4], 10);
		const period = match[7];

		if (period)
		{
			if (/pm/i.test(period) && hours < 12) hours = hours + 12;
			if (/am/i.test(period) && hours === 12) hours = 0;
		}

		return new Date(
			parseInt(match[1], 10),
			parseInt(match[2], 10) - 1,
			parseInt(match[3], 10),
			hours,
			parseInt(match[5], 10),
			match[6] ? parseInt(match[6], 10) : 0
		);
	}

	const parsed = new Date(value);
	return isNaN(parsed.getTime()) ? null : parsed;
}




/**
 * Convert an API local date/time to a day minute against the timesheet's
 * reporting date ("yyyy-MM-dd"), so times after midnight keep running upwards.
 *
 * @returns {number|null} `null` when either value can't be parsed.
 */
export function toDayMinutes(value, reportingDateYyyyMmDd)
{
	const dateTime = parseLocalDateTime(value);
	if (!dateTime) return null;

	const baseDate = parseLocalDateTime(reportingDateYyyyMmDd + " 00:00");

	if (!baseDate)
	{
		// No reporting date to measure against — fall back to the minute of the day.
		return dateTime.getHours() * 60 + dateTime.getMinutes();
	}

	return Math.round((dateTime.getTime() - baseDate.getTime()) / 60000);
}




/**
 * Parse a duration the user typed in the manual-entry form.
 * Accepts whole minutes ("52"), H:MM ("0:52") and decimal hours ("0.9").
 *
 * @returns {number|null} Minutes, or `null` when the text isn't a duration.
 */
export function parseDurationInput(text)
{
	const value = String(text || "").trim();

	if (/^\d+$/.test(value)) return parseInt(value, 10);

	const hoursAndMinutes = value.match(/^(\d+):([0-5]\d)$/);
	if (hoursAndMinutes) return parseInt(hoursAndMinutes[1], 10) * 60 + parseInt(hoursAndMinutes[2], 10);

	if (/^\d*\.\d+$/.test(value) || /^\d+\.\d*$/.test(value)) return Math.round(parseFloat(value) * 60);

	return null;
}




/**
 * Parse a clock time the user typed in the manual-entry form ("2:00 PM", "14:00").
 *
 * @returns {number|null} Minute of the day, or `null` when the text isn't a time.
 */
export function parseTimeInput(text)
{
	const value = String(text || "").trim();

	const twelveHour = value.match(/^(\d{1,2}):([0-5]\d)\s*(AM|PM)$/i);

	if (twelveHour)
	{
		let hours = parseInt(twelveHour[1], 10) % 12;
		if (/pm/i.test(twelveHour[3])) hours = hours + 12;

		return hours * 60 + parseInt(twelveHour[2], 10);
	}

	const twentyFourHour = value.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
	if (twentyFourHour) return parseInt(twentyFourHour[1], 10) * 60 + parseInt(twentyFourHour[2], 10);

	return null;
}




/**
 * Round minutes up to whole 6-minute billing units.
 */
export function roundToBillingUnits(minutes)
{
	return Math.ceil(Math.max(0, minutes || 0) / 6) * 6;
}




/**
 * The "bills as" preview for a duration — "54m (0.9 h)".
 */
export function formatBillingUnits(minutes)
{
	const billed = roundToBillingUnits(minutes);

	return formatMinutes(billed) + " (" + Math.round((billed / 60) * 10) / 10 + " h)";
}




/**
 * A part/whole share as a CSS width for a segmented bar.
 */
export function shareToCssWidth(part, whole)
{
	if (!whole) return "0%";

	return ((part / whole) * 100) + "%";
}




/**
 * A part/whole share as a whole percentage — "84%".
 */
export function formatPercent(part, whole)
{
	if (!whole) return "0%";

	return Math.round((part / whole) * 100) + "%";
}




/**
 * Just the noun, singular or plural against a count — (1, "block") -> "block",
 * (3, "block") -> "blocks". Split out from `formatCount()` so a caller lining
 * numbers up in a column can right-align the number on its own and left-align
 * this afterwards, rather than right-aligning "N block"/"N blocks" as one
 * string, which shifts the number depending on which word length wins.
 */
export function pluraliseWord(count, singular, plural = null)
{
	if ((count || 0) === 1) return singular;

	return plural || singular + "s";
}


/**
 * Pluralise a noun against a count — (1, "block") -> "1 block".
 */
export function formatCount(count, singular, plural = null)
{
	const total = count || 0;

	return total + " " + pluraliseWord(total, singular, plural);
}




/**
 * Strip the folder path off a captured process name
 * ("C:\Program Files\...\chrome.exe" -> "chrome.exe").
 */
export function processShortName(processName)
{
	const name = String(processName || "").replace(/\\/g, "/");

	return name.substring(name.lastIndexOf("/") + 1);
}
